/*!
 * Memoize-fetch shim for /products/<handle>.js
 *
 * Background:
 *   On every PDP, multiple scripts (Stiletto's `getProduct` in theme.js
 *   L4345, plus one or more 3rd-party apps — Smile.io / Klaviyo /
 *   Hengam / Elevar) independently fetch `/products/<handle>.js` to
 *   read the full product JSON. The Shopify CDN doesn't cache this
 *   response in the browser (transferSize > 0 on every hit), so the
 *   same payload gets fetched 3+ times sequentially per PDP load —
 *   ~2 seconds of cumulative wall-clock cost.
 *
 *   Browsers natively de-dupe fetches that fire WITHIN the same micro-
 *   task tick, but Stiletto + 3rd-party apps fire at different times
 *   (theme init, app delayed-init, post-DOMContentLoaded), so the
 *   native dedupe doesn't help.
 *
 * Fix:
 *   Wrap `window.fetch` to memoize requests for `/products/<handle>.js`
 *   for 60 seconds. Any caller arriving while the fetch is in-flight,
 *   OR within 60 s of a successful response, gets the cached Response
 *   (cloned per caller, since Response bodies are single-use streams).
 *   Falls back to native fetch for everything else.
 *
 * Why this is safe:
 *   - Memoization key includes the URL only — different `init` params
 *     (headers, body) would still hit the same cache, but for product
 *     JSON GETs with no body that's a non-issue.
 *   - 60 s TTL means stale data is rare; price/variant changes
 *     mid-session would be picked up on the next minute.
 *   - Loading this shim BEFORE theme.js + 3rd-party apps means it
 *     intercepts every caller. Loaded synchronously in <head> via
 *     `{{ ... | asset_url | script_tag }}` for that reason.
 *   - If a future caller passes `init` that genuinely needs a fresh
 *     network hit, they can use `init.cache = 'no-store'` to bypass
 *     this shim — we honor that flag.
 *
 * Single-init guard via `window.__dedupeProductFetchInit`. Idempotent.
 */

(function () {
  if (window.__dedupeProductFetchInit) return;
  window.__dedupeProductFetchInit = true;

  if (typeof window.fetch !== 'function') return;

  var nativeFetch = window.fetch.bind(window);
  var inFlight = new Map();   // cacheKey -> Promise<Response>
  var cache = new Map();      // cacheKey -> { response: Response, expires: ms }
  var TTL_MS = 60 * 1000;     // 60 s — long enough to dedupe theme + app inits

  // Match `/products/<handle>.js` (with optional query / hash).
  // Excludes paths like `/products/<handle>.js.something` (no such pattern in Shopify, but defensive).
  var PRODUCT_JS_RE = /\/products\/[a-z0-9_-]+\.js(?:[?#]|$)/i;

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;  // Request object
    return '';
  }

  /**
   * Compute the cache key for a `/products/<handle>.js` URL.
   *
   * PR-PERF-DEDUPE-CACHE-KEY (2026-05-07):
   * Shopify ignores query strings on the `/products/<handle>.js` endpoint —
   * the response is identical regardless of `?variant=`, cache-buster, or
   * any other param. The previous cache key was the full URL string, so
   * callers that fired `?variant=123` and `?cb=456` and the bare URL
   * each got their own cache slot — observed 86% dedup hit rate on live.
   *
   * Stripping the query string from the cache key (but still passing the
   * caller's full URL through to the underlying network) closes that
   * gap: every variation of `/products/foo.js?...` collapses to the same
   * entry. Expected to push dedup hit rate to ~100%.
   *
   * Caller still gets a Response cloned from the original network fetch,
   * which is correct because Shopify's response doesn't depend on the
   * query string anyway.
   */
  function cacheKeyOf(url) {
    var qIdx = url.indexOf('?');
    var hIdx = url.indexOf('#');
    if (qIdx === -1 && hIdx === -1) return url;
    var cut = qIdx;
    if (hIdx !== -1 && (cut === -1 || hIdx < cut)) cut = hIdx;
    return url.slice(0, cut);
  }

  function shouldDedupe(input, init) {
    if (init && init.cache === 'no-store') return false;
    var u = urlOf(input);
    return PRODUCT_JS_RE.test(u);
  }

  window.fetch = function (input, init) {
    if (!shouldDedupe(input, init)) {
      return nativeFetch(input, init);
    }

    var url = urlOf(input);
    var key = cacheKeyOf(url);

    // Check 60 s memo
    var entry = cache.get(key);
    if (entry && entry.expires > Date.now()) {
      return Promise.resolve(entry.response.clone());
    }

    // Check in-flight
    var pending = inFlight.get(key);
    if (pending) {
      return pending.then(function (r) { return r.clone(); });
    }

    // Fresh fetch — pass the caller's original input/init through unchanged
    // so Shopify still sees the exact URL they wanted (we only normalize
    // the CACHE KEY, not the network request).
    var promise = nativeFetch(input, init).then(function (r) {
      // Cache successful responses only. Errored responses fall through
      // to retry on next call (don't poison the cache with 5xx).
      if (r && r.ok) {
        cache.set(key, { response: r.clone(), expires: Date.now() + TTL_MS });
      }
      inFlight.delete(key);
      return r.clone();
    }, function (err) {
      inFlight.delete(key);
      throw err;
    });

    inFlight.set(key, promise);
    return promise;
  };
})();
